import { useEffect, useRef, useState } from "react";
import {
  DRAWING_TOOL_COLORS,
  DrawingToolColor,
  getImageUrl,
  ImageType,
  DrawingToolMode,
  DrawingTool
} from "duck";
import { useWindowSize } from "../index";
import {
  computePointInCanvas,
  toggleIOsBounceEffect,
  HISTORY_STORAGE_SIZE
} from "./duck";

const useDrawing = (
  canvasWidth: string,
  canvasHeight: string,
  value?: number | ImageType | null,
  onStopDrawing?: (base64: string) => void
): DrawingTool => {
  const { width, height } = useWindowSize();
  const [color, setColor] = useState(DRAWING_TOOL_COLORS[0]);
  const [penWidth, setPenWidth] = useState(2);
  const [mode, setMode] = useState<DrawingToolMode>("pen");

  const history = useRef<ImageData[]>([]);
  const historyIndex = useRef(-1);

  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const isDrawingRef = useRef(false);
  const prevPointRef = useRef<{ x: number; y: number } | null>(null);

  const isBase64Value =
    value != null && typeof value === "object" && "base64Format" in value;

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;

      canvas.style.width = canvasWidth;
      canvas.style.height = canvasHeight;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (context) {
        context.scale(1, 1);
        contextRef.current = context;

        if (value) {
          const imageUrl = isBase64Value
            ? value.base64Format
            : getImageUrl(value);
          const img = new Image();
          img.src = imageUrl;
          img.crossOrigin = "anonymous";
          img.onload = () => {
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            history.current.push(
              context.getImageData(0, 0, canvas.width, canvas.height)
            );
            historyIndex.current += 1;
          };
          return;
        }

        if (canvas?.width) {
          history.current.push(
            context.getImageData(0, 0, canvas.width, canvas.height)
          );
          historyIndex.current += 1;
        }
      }
    }
    // TODO: get rid of ref.current from dependencies array
  }, [width, height, canvasRef.current]);

  const startDrawing = () => {
    if (mode === "disabled") {
      return;
    }
    isDrawingRef.current = true;
    toggleIOsBounceEffect(false);
  };

  const draw = ({ nativeEvent }: { nativeEvent: MouseEvent | TouchEvent }) => {
    if (!isDrawingRef.current) {
      return;
    }

    nativeEvent.preventDefault();

    const point = computePointInCanvas(nativeEvent, canvasRef.current);
    const ctx = contextRef.current;

    if (point && ctx) {
      const start = prevPointRef.current ?? point;

      ctx.globalCompositeOperation =
        mode === "pen" ? "source-over" : "destination-out";

      // draw background line with opacity to smooth primary line's edges
      ctx.beginPath();
      ctx.strokeStyle = color.rgba;
      ctx.lineWidth = penWidth + 2;
      ctx.lineCap = "butt";
      ctx.lineJoin = "miter";
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();

      // draw primary line without opacity
      ctx.beginPath();
      ctx.strokeStyle = color.name;
      ctx.lineWidth = penWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();

      // draw circle to smooth line
      ctx.fillStyle = color.name;
      ctx.beginPath();
      ctx.arc(start.x, start.y, penWidth / 2, 0, 2 * Math.PI);
      ctx.fill();

      prevPointRef.current = point;
    }
  };

  const stopDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (mode === "disabled") {
      return;
    }
    if (canvasRef.current && isDrawingRef.current && onStopDrawing) {
      const base64 = canvasRef.current.toDataURL();
      onStopDrawing(base64);
    }
    isDrawingRef.current = false;
    prevPointRef.current = null;
    toggleIOsBounceEffect(true);

    if (contextRef.current && canvasRef.current && e.type !== "mouseleave") {
      const lastChange = contextRef.current.getImageData(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      // +1 for the first rendered image
      history.current = [...history.current, lastChange].slice(
        -(HISTORY_STORAGE_SIZE + 1)
      );
      historyIndex.current = history.current.length - 1;
    }
  };

  const undo = () => {
    if (historyIndex.current > 0) {
      historyIndex.current -= 1;
      contextRef.current?.putImageData(
        history.current[historyIndex.current],
        0,
        0
      );
    }
  };

  const redo = () => {
    if (historyIndex.current < history.current.length - 1) {
      historyIndex.current += 1;
      contextRef.current?.putImageData(
        history.current[historyIndex.current],
        0,
        0
      );
    }
  };

  const clear = () => {
    if (contextRef.current && canvasRef.current) {
      const w = canvasRef.current.width;
      const h = canvasRef.current.height;
      contextRef.current.clearRect(0, 0, w, h);
      historyIndex.current = 0;
      history.current = [contextRef.current.getImageData(0, 0, w, h)];
    }
  };

  return {
    canvasRef,
    contextRef,
    startDrawing,
    stopDrawing,
    draw,
    toolsConfig: {
      color,
      updateColor: (c: DrawingToolColor) => setColor(c),
      penWidth,
      updatePenWidth: (w: number) => setPenWidth(w),
      mode,
      updateMode: (m: DrawingToolMode) => setMode(m),
      undo,
      redo,
      clear
    }
  };
};

export default useDrawing;
